import { useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import { Check, X, PencilSimple, CircleNotch } from '@phosphor-icons/react';
import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

interface Props {
  /** Bepaalt welk endpoint gebruikt wordt: contactpersoon of firma. */
  type: 'contact' | 'company';
  id: number;
  name: string;
  /** Wordt aangeroepen met het bewaarde adres zodat de lijst kan bijwerken. */
  onSaved: (email: string) => void;
  /** Compacte weergave voor in een smalle dropdown. */
  compact?: boolean;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Vult ter plekke het ontbrekende e-mailadres van een contactpersoon of firma aan en
 * bewaart dat op de fiche zelf (PUT /contact/:id/email of /contact/company/:id/email),
 * zodat het adres ook voor volgende mails en andere modules bekend is.
 */
export function RenoAddEmailInline({ type, id, name, onSaved, compact = false }: Props) {
  const { session } = useSession();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const label = type === 'company' ? 'firma' : 'contactpersoon';

  const handleSave = async () => {
    const email = value.trim();
    if (!EMAIL_PATTERN.test(email)) {
      setError('Geen geldig e-mailadres');
      return;
    }
    if (!session) return;

    setIsSaving(true);
    setError(null);
    try {
      const url =
        type === 'company'
          ? `${API_URL}/contact/company/${id}/email`
          : `${API_URL}/contact/${id}/email`;
      const res = await fetchWithAuth(
        url,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) },
        session,
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Opslaan mislukt (${res.status})`);
      }

      toast.success(`E-mailadres bewaard bij ${name}`);
      setIsEditing(false);
      setValue('');
      onSaved(email);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { component: 'RenoAddEmailInline' },
        extra: { type, id, email },
      });
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isEditing) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
        title={`E-mailadres toevoegen bij deze ${label}`}
        className={`flex items-center gap-1 rounded border border-amber-300 bg-white px-1.5 text-amber-900 transition-colors hover:bg-amber-100 ${
          compact ? 'h-5 text-[10px]' : 'h-6 text-xs'
        }`}
      >
        <PencilSimple size={compact ? 10 : 12} />
        E-mailadres toevoegen
      </button>
    );
  }

  return (
    <div
      className="flex flex-col gap-1"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-1">
        <input
          autoFocus
          type="email"
          value={value}
          disabled={isSaving}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') {
              setIsEditing(false);
              setError(null);
            }
          }}
          placeholder={`E-mailadres van ${name}`}
          className={`min-w-0 flex-1 rounded border border-input bg-background px-1.5 text-neutral-primary outline-none focus:border-moss-primary ${
            compact ? 'h-6 text-[11px]' : 'h-7 text-xs'
          }`}
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          title="Bewaren"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-moss-primary text-white transition-colors hover:bg-moss-primary/90 disabled:opacity-60"
        >
          {isSaving ? <CircleNotch size={12} className="animate-spin" /> : <Check size={12} weight="bold" />}
        </button>
        <button
          type="button"
          onClick={() => {
            setIsEditing(false);
            setError(null);
          }}
          disabled={isSaving}
          title="Annuleren"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-input text-neutral-secondary-mid transition-colors hover:bg-muted"
        >
          <X size={12} />
        </button>
      </div>
      {error && <span className="text-[10px] text-status-bad">{error}</span>}
    </div>
  );
}
